import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import styles from "../styles/Merchant.module.css";
import qrStyles from "../styles/QR.module.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FAQSection from "../components/home/FAQSection";
import { Link, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
import {
  FaFileInvoiceDollar,
  FaQrcode,
  FaReceipt,
  FaPercent,
  FaSms,
  FaEnvelope,
  FaUndo,
  FaBan,
  FaBoxes,
  FaCreditCard,
  FaShoppingCart,
  FaCashRegister,
  FaStore,
  FaUtensils,
  FaShoppingBasket,
  FaBriefcase,
  FaBuilding,
  FaWarehouse,
  FaSitemap,
  FaGlobe,
  FaDownload,
  FaBookOpen,
} from "react-icons/fa";

const MANUAL_PATH = "/assets/E-Invoice_User_Manual.pdf";

const fadeUp = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.55, ease: "easeOut" },
  },
};

function revealProps(reduceMotion, delay = 0) {
  if (reduceMotion) return {};
  return {
    variants: fadeUp,
    initial: "hidden",
    whileInView: "visible",
    viewport: { once: true, amount: 0.22 },
    transition: { duration: 0.55, ease: "easeOut", delay },
  };
}

const introHighlights = [
  { label: "IRN registration", icon: FaFileInvoiceDollar },
  { label: "Cash & credit sales", icon: FaReceipt },
  { label: "SMS & email", icon: FaSms },
];

const features = [
  {
    title: "Electronic Invoice Registration",
    desc: "Register customer invoices with the Ministry of Revenue and receive an Invoice Reference Number (IRN) as proof of registration. The system supports B2C, B2B, and B2G invoices.",
    icon: FaFileInvoiceDollar,
  },
  {
    title: "Cash & Credit Sales",
    desc: "Support both immediate cash transactions and credit sales. Cash payments can be submitted as sales receipts, while credit payments can be reported as customers make partial or full payments through Receipt Reference Numbers (RRN).",
    icon: FaReceipt,
  },
  {
    title: "Withholding Tax Management",
    desc: "Manage withholding tax during sales and vendor transactions, including withholding receipt registration.",
    icon: FaPercent,
  },
  {
    title: "Credit & Debit Notes",
    desc: "Manage returns, refunds, corrections, and additional charges through credit and debit notes linked to the original invoice.",
    icon: FaUndo,
  },
  {
    title: "Invoice Cancellation",
    desc: "Cancel registered invoices according to the applicable workflow while maintaining the transaction record.",
    icon: FaBan,
  },
  {
    title: "Automatic SMS & Email Receipts",
    desc: "Keep customers informed automatically. Customers can receive notifications for invoice registration, sales receipts, credit and debit notes, withholding receipts, and invoice cancellations.",
    icon: FaEnvelope,
  },
  {
    title: "QR Code & Digital Verification",
    desc: "Registered invoices include the relevant IRN and QR code, providing customers with accessible electronic transaction information.",
    icon: FaQrcode,
  },
  {
    title: "Product & Inventory Management",
    desc: "Register products, manage stock, configure product attributes and modifiers, and receive low-stock alerts.",
    icon: FaBoxes,
  },
  {
    title: "Multiple Payment Methods",
    desc: "Support different payment methods including cash, bank transfer, mobile wallet, card payment, and credit sale.",
    icon: FaCreditCard,
  },
  {
    title: "Sales & Receipt Management",
    desc: "Create sales, manage carts, apply discounts, generate invoices and receipts, search previous transactions, and reprint receipts when required.",
    icon: FaShoppingCart,
  },
  {
    title: "Shift & Cash Management",
    desc: "Manage cashier operations from opening to closing: opening cash balance, shift closing, shift summaries, shift history, and cash variance reporting.",
    icon: FaCashRegister,
  },
];

const getStartedSteps = [
  {
    step: 1,
    title: "Set Up Your Business",
    desc: "Register your business, branch, users, and required system information.",
  },
  {
    step: 2,
    title: "Configure MOR Integration",
    desc: "Connect your business or branch to the Ministry of Revenue using the required credentials and digital certificates.",
  },
  {
    step: 3,
    title: "Create a Sale",
    desc: "Add products, select the customer, choose the payment method, and complete the transaction.",
  },
  {
    step: 4,
    title: "Register Your Invoice",
    desc: "Submit the invoice for registration and receive your IRN, QR code, and registration details.",
  },
  {
    step: 5,
    title: "Register Payment",
    desc: "Register cash or credit payments and generate the appropriate sales receipt and RRN.",
  },
  {
    step: 6,
    title: "Notify Your Customer",
    desc: "Customers can automatically receive their invoice and payment information through SMS or email.",
  },
];

const audiences = [
  {
    title: "Retail Businesses",
    desc: "Manage products, sales, payments, invoices, and inventory from one platform.",
    icon: FaStore,
  },
  {
    title: "Restaurants",
    desc: "Manage sales, products, modifiers, payments, receipts, and daily operations.",
    icon: FaUtensils,
  },
  {
    title: "Supermarkets",
    desc: "Support high-volume transactions while maintaining organized sales and inventory records.",
    icon: FaShoppingBasket,
  },
  {
    title: "Service Businesses",
    desc: "Create invoices, manage customers, receive payments, and maintain electronic transaction records.",
    icon: FaBriefcase,
  },
  {
    title: "Multi-Branch Businesses",
    desc: "Manage multiple business branches and maintain organized operational and e-invoicing processes.",
    icon: FaBuilding,
  },
];

const comingSoon = [
  {
    title: "Multi-Branch Management",
    desc: "Manage multiple business branches from one connected platform.",
    icon: FaSitemap,
  },
  {
    title: "Warehouse Management",
    desc: "Manage inventory movement and warehouse operations.",
    icon: FaWarehouse,
  },
  {
    title: "Unified POS Integration",
    desc: "Bring sales registration and POS operations together through a unified experience.",
    icon: FaCashRegister,
  },
  {
    title: "Localization",
    desc: "Support for regional settings, local languages, and business-specific configurations.",
    icon: FaGlobe,
  },
];

const faqs = [
  {
    question: "What is Arif Electronic Sales Registration System?",
    answer:
      "It is an integrated system for sales, electronic invoices, payments, receipts, inventory, and daily operations. It is built to simplify those operations and support Ministry of Revenue (MOR) e-invoicing requirements.",
  },
  {
    question: "What do I receive when an invoice is registered?",
    answer:
      "Customer invoices are registered with the Ministry of Revenue, and the system returns an Invoice Reference Number (IRN) as proof of registration, together with a QR code. B2C, B2B, and B2G invoices are supported.",
  },
  {
    question: "How are cash and credit sales recorded?",
    answer:
      "Cash payments are submitted as sales receipts. Credit payments are reported as customers make partial or full payments, each with a Receipt Reference Number (RRN).",
  },
  {
    question: "How do customers receive their documents?",
    answer:
      "Customers can be notified automatically by SMS and email for invoice registration, sales receipts, credit and debit notes, withholding receipts, and invoice cancellations.",
  },
  {
    question: "How long are audit logs kept?",
    answer:
      "Audit logs are kept for at least 90 days from the date they are created. They are kept longer when the Ministry of Revenues, tax law, or an ongoing investigation, audit, or legal proceeding requires it.",
  },
  {
    question: "What is stored in an audit log, and who can open it?",
    answer:
      "Each record includes a user identifier, date and time with time zone, the action, the system component, an IP address or device identifier, and whether the action succeeded or failed. Exchanges with the Ministry’s Electronic Invoice Registration System also record the transaction identifier, the data sent, and the response received. Access is limited to authorised roles — system administrators, security, compliance, internal auditors, external auditors with prior authorisation, and personnel authorised by the Ministry of Revenues — on a need-to-know basis.",
  },
  {
    question: "How are audit logs protected if something fails?",
    answer:
      "Logs are stored in a secure repository separate from production systems, in a write-once format or an equivalent that blocks unauthorised changes, with cryptographic hashing and regular integrity checks. They are replicated to a secondary data centre. The recovery target is no more than 24 hours of logs from the previous backup cycle, and logs are to be available within 4 hours of a declared failure. Unauthorised access is treated as a security incident.",
  },
  {
    question: "Can I export my data or move it to another provider?",
    answer:
      "Yes. You can export your data at any time, not only when you leave, in a common machine-readable format such as CSV, XML, or JSON. An export includes invoices, transaction records, customer and supplier information, tax information, and the logs that relate to you. The first export is free and is processed within 5 working days. You can also transfer that data to another service provider. The first transfer is free and is completed within 30 days.",
  },
  {
    question: "Can I ask for my data to be deleted?",
    answer:
      "When the service ends, you can request deletion in writing. Deletion covers the data generated, processed, stored, or transmitted for you through the system, and it is completed within 30 days, with written confirmation. Data that the law requires Arifpay to keep is retained for that period, and you are told what is being kept.",
  },
  {
    question: "What happens if the service ends?",
    answer:
      "If accreditation is cancelled, user taxpayers are notified within 10 days. If Arifpay intends to end the service, the Authority and user taxpayers are notified 6 months in advance. In both cases you can retrieve or transfer your data within 6 months. Transition support — export and transfer help, technical guidance, documentation, and a dedicated contact — is provided free of charge.",
  },
];

const supportFaqs = [
  {
    question: "What is IRN?",
    answer:
      "IRN is the invoice number MOR gives. Has IRN = OK. No IRN = problem.",
  },
  {
    question: "What is RRN?",
    answer:
      "RRN is the receipt number MOR gives when a payment is recorded.",
  },
  {
    question: "What does Status “Registered” or “Not Registered” mean?",
    answer: "Registered = OK. Not Registered = not sent or has an error.",
  },
  {
    question: "What should I do when I have a problem?",
    answer:
      "1. Note your company name and invoice number. 2. Read the error message or take a screenshot. 3. Check the common fixes below. 4. If the issue is about token, certificate, server, or is not listed here, contact support. 5. If it is still not fixed after trying the steps, contact the technical support team.",
  },
  {
    question: "What should I never do?",
    answer:
      "Never share your passwords, secret keys, or certificate files. Never create the same invoice again if you are not sure it failed, because this can create duplicates. Never expect a registered invoice to be cancelled at MOR without support. Never change system settings you are not authorized to change.",
  },
  {
    question: "My invoice has no QR code / no IRN.",
    answer:
      "Check if the invoice is confirmed—draft invoices are not sent to MOR. If it is a draft, click Confirm. If it is confirmed, open the invoice and read the Rule Errors message. Your invoice must be confirmed first. Then the system sends it to MOR and the QR code appears. If there is no error message, or the message talks about token, certificate, or server, contact the technical support team.",
  },
  {
    question: "Error says: “Buyer TIN is required.”",
    answer:
      "The buyer is a company, and their TIN is missing. Open the customer contact and type the TIN (10 digits). Save it, go back to the invoice, and send it again. MOR needs your customer’s TIN. If it still does not work, contact the technical support team.",
  },
  {
    question: "Error says: “Seller TIN is missing.”",
    answer:
      "This is your company’s TIN. Your administrator must open Settings → Companies and add the TIN. Then send the invoice again. If the TIN is already there, contact the technical support team.",
  },
  {
    question: "Error is about address (Region, City, Woreda...).",
    answer:
      "Open the customer contact. Fill in all address fields: Region, Zone, City, Sub-City, Woreda. Save, then send the invoice again. MOR needs the full address of your customer. If it still does not work, contact the technical support team.",
  },
  {
    question: "Error is about tax, price, or quantity.",
    answer:
      "Check every product line on the invoice. Each line needs a tax, for example VAT 15%. Quantity and price must be more than 0. Delete empty lines, then send again. If everything looks correct but it still fails, contact the technical support team.",
  },
  {
    question: "I made a mistake. How do I change the invoice?",
    answer:
      "A registered invoice cannot be edited. This is MOR’s rule. Open the invoice, click Credit Note, and write the reason. Then create a new correct invoice. If you need to fully cancel the invoice at MOR, contact the technical support team.",
  },
  {
    question: "The customer paid, but there is no receipt (RRN).",
    answer:
      "Make sure you registered the payment in the system. Also check that the invoice has a QR code / IRN—if not, fix that first. Register the payment on the invoice, then MOR gives a receipt number. If it still does not work, contact the technical support team.",
  },
  {
    question: "I can’t see the E-Invoice tab or buttons.",
    answer:
      "Check the company/branch name at the top right of the screen. If it is the wrong one, switch to the correct company. If it is correct, ask your admin to check your access rights. If it still does not work, contact the technical support team.",
  },
  {
    question: "The system is slow / no internet.",
    answer:
      "Check your internet connection. Wait a few minutes, then send the invoice again. A duplicate will not be created. The invoice needs internet to reach MOR. If the internet works but all invoices fail, contact the technical support team urgently.",
  },
  {
    question: "When should I contact the technical support team?",
    answer:
      "Contact support the same day for errors about token, Client ID, API key or access; certificate, private key, CSR or OpenSSL; server, API response, conversationId or Unexpected error; Document Number; when the system says Registered but MOR says it doesn’t exist (or the opposite); if you need to cancel an invoice at MOR; or if POS or mobile app invoices are not registering. Contact support within 2 days for a new company, new branch, TIN change, or new certificate, or if you tried the common fixes and it still doesn’t work. Contact support urgently (within 1 hour) if the same invoice was registered twice, or if all invoices fail.",
  },
  {
    question: "What happens after I contact support?",
    answer:
      "Support will review your case and contact you. Please don’t create the same invoice again in the meantime.",
  },
  {
    question: "What information should I provide when contacting support?",
    answer:
      "Company name, Company TIN, Branch, Contact person & phone, Invoice number, IRN (if it has one), Status shown (Registered / Not Registered), Error message (exact words), Date & time of problem, What you already tried, and a screenshot if available.",
  },
];

const supportChecklist = [
  "Company name / TIN",
  "Invoice number / IRN / RRN",
  "Exact error message",
  "Date / time",
  "Screenshot",
  "What you already tried",
];

const EInvoice = () => {
  const location = useLocation();
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setPageWidth] = useState(680);
  const [pageSlotHeight, setPageSlotHeight] = useState(null);
  const canvasWrapRef = useRef(null);
  const reduceMotion = useReducedMotion();

  const rememberPageHeight = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const nextHeight = Math.round(canvas.getBoundingClientRect().height);
    if (nextHeight > 0) setPageSlotHeight(nextHeight);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const updateWidth = () => {
      const max = 720;
      const fit = Math.min(max, window.innerWidth - 96);
      setPageWidth(Math.max(240, fit));
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  return (
    <div>
      <Header />
      <div className={`${styles.equbPage} ${styles.einvoicePage}`}>
        <div
          className={`${qrStyles.empoweringSection} ${qrStyles.equbHeroSection} ${qrStyles.einvoiceHero} ${styles.einvoiceHeroPad}`}
        >
          <motion.div
            className={qrStyles.hero}
            {...(reduceMotion
              ? {}
              : {
                  initial: { opacity: 0 },
                  animate: { opacity: 1 },
                  transition: { duration: 0.6, ease: "easeOut" },
                })}
          >
            <div className={qrStyles.heroContent}>
              <p className={qrStyles.msmesLabel}>
                Arif Electronic Sales Registration System
              </p>
              <h1 className={qrStyles.heroTitle}>
                Smart, Secure & Compliant Sales Registration for Your Business
              </h1>
              <p className={qrStyles.equbHeroDesc}>
                Manage your sales, electronic invoices, payments, receipts,
                inventory, and daily business operations with Arif Electronic
                Sales Registration System — an integrated solution designed to
                simplify business operations and support Ministry of Revenue
                (MOR) e-invoicing requirements.
              </p>
              <div className={styles.einvoiceHeroActions}>
                <a href="#user-manual" className={qrStyles.contactButton}>
                  Read the manual
                </a>
                <Link to="/ContactUs" className={styles.einvoiceHeroSecondary}>
                  Contact Us →
                </Link>
              </div>
            </div>
            <div className={qrStyles.heroImage}>
              <img
                className={styles.einvoiceHeroImage}
                src="/assets/Invoice-landing.png"
                alt="Arif Electronic Sales Registration System"
              />
            </div>
          </motion.div>
        </div>

        <motion.section
          className={styles.equbFeaturesIntro}
          {...revealProps(reduceMotion)}
        >
          <div className={styles.equbFeaturesIntroCard}>
            <span className={styles.equbFeaturesIntroBadge}>KEY FEATURES</span>
            <h3 className={`${styles.equbIntroTitle} ${styles.einvoiceIntroTitle}`}>
              Sales, invoices, payments, receipts, and inventory
            </h3>
            <p className={styles.equbIntroPara}>
              An integrated solution designed to simplify business operations
              and support Ministry of Revenue (MOR) e-invoicing requirements.
            </p>
            <div className={styles.equbFeaturesIntroPills}>
              {introHighlights.map(({ label, icon }) => {
                const HighlightIcon = icon;
                return (
                  <span key={label} className={styles.equbFeaturesIntroPill}>
                    <HighlightIcon aria-hidden />
                    {label}
                  </span>
                );
              })}
            </div>
          </div>
        </motion.section>

        <section className={styles.equbFeaturesSection}>
          <div className={styles.equbFeaturesGrid}>
            {features.map(({ title, desc, icon }, index) => {
              const FeatureIcon = icon;
              return (
                <motion.article
                  key={title}
                  className={styles.equbFeatureCard}
                  {...revealProps(reduceMotion, index * 0.06)}
                >
                  <div className={styles.equbFeatureIconWrap}>
                    <FeatureIcon aria-hidden />
                  </div>
                  <div className={styles.equbFeatureCardBody}>
                    <h4 className={styles.equbFeatureCardTitle}>{title}</h4>
                    <p className={styles.equbFeatureCardDesc}>{desc}</p>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </section>

        <motion.section {...revealProps(reduceMotion)}>
          <div className={styles.topHeader}>
            <p className={styles.equbSectionLabel}>HOW IT WORKS</p>
            <h2 className={styles.equbSectionTitle}>
              Take control of your sales
            </h2>
          </div>
          <div className={`${styles.equbStepsGrid} ${styles.einvoiceStepsGrid}`}>
            {getStartedSteps.map(({ step, title, desc }, index) => (
              <motion.div
                key={step}
                style={{ textAlign: "left" }}
                {...revealProps(reduceMotion, index * 0.05)}
              >
                <div className={styles.equbStepBadge}>{step}</div>
                <h4 className={styles.equbStepTitle}>{title}</h4>
                <p className={styles.equbStepDesc}>{desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section {...revealProps(reduceMotion)}>
          <div className={styles.topHeader}>
            <p className={styles.equbSectionLabel}>
              ONE SYSTEM. COMPLETE SALES MANAGEMENT.
            </p>
            <h2 className={styles.equbSectionTitle}>
              From Sales Registration to Electronic Invoicing
            </h2>
          </div>
          <div className={styles.einvoiceStatement}>
            <p>
              Arif Electronic Sales Registration System connects sales
              operations, invoicing, payments, receipts, inventory, and
              reporting in one integrated platform.
            </p>
            <p className={styles.einvoiceMotto}>
              Register. Sell. Invoice. Track.
            </p>
          </div>
        </motion.section>

        <motion.section
          className={styles.equbAudienceSection}
          {...revealProps(reduceMotion)}
        >
          <div className={styles.topHeader}>
            <p className={styles.equbSectionLabel}>BUILT FOR YOUR BUSINESS</p>
          </div>
          <div className={`${styles.equbAudienceGrid} ${styles.einvoiceAudienceGrid}`}>
            {audiences.map(({ title, desc, icon }, index) => {
              const AudienceIcon = icon;
              return (
                <motion.article
                  key={title}
                  className={styles.equbAudienceCard}
                  {...revealProps(reduceMotion, index * 0.06)}
                >
                  <div className={styles.equbAudienceIconWrap}>
                    <AudienceIcon aria-hidden />
                  </div>
                  <div className={styles.equbAudienceCardBody}>
                    <h4 className={styles.equbAudienceTitle}>{title}</h4>
                    <p className={styles.equbAudienceDesc}>{desc}</p>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </motion.section>

        <motion.section {...revealProps(reduceMotion)}>
          <div className={styles.topHeader}>
            <p className={styles.equbSectionLabel}>DIGITAL SALES. SIMPLIFIED.</p>
            <h2 className={styles.equbSectionTitle}>
              Make every transaction easier to manage.
            </h2>
          </div>
          <div className={styles.einvoiceStatement}>
            <p>
              With Arif Electronic Sales Registration System, businesses can
              manage their daily sales while connecting their invoicing
              processes with the Ministry of Revenue.
            </p>
          </div>
        </motion.section>

        <motion.section {...revealProps(reduceMotion)}>
          <div className={styles.topHeader}>
            <p className={styles.equbSectionLabel}>GET STARTED</p>
            <h2 className={`${styles.equbSectionTitle} ${styles.einvoiceGetStartedTitle}`}>
              Start managing your sales and electronic invoices
              <br className={styles.mobileBreak} /> with Arif Electronic Sales
              Registration System.
            </h2>
          </div>
          <div className={styles.einvoiceCta}>
            {/* <Link to="/ContactUs" className={qrStyles.contactButton}>
              Get Started →
            </Link> */}
            <Link to="/ContactUs" className={styles.einvoiceHeroSecondary}>
              Contact Us →
            </Link>
          </div>
        </motion.section>

        <motion.section
          className={styles.equbAudienceSection}
          {...revealProps(reduceMotion)}
        >
          <div className={styles.topHeader}>
            <p className={styles.equbSectionLabel}>COMING SOON</p>
          </div>
          <div className={`${styles.equbAudienceGrid} ${styles.einvoiceAudienceGrid}`}>
            {comingSoon.map(({ title, desc, icon }, index) => {
              const SoonIcon = icon;
              return (
                <motion.article
                  key={title}
                  className={`${styles.equbAudienceCard} ${styles.equbAudienceCardSoon}`}
                  {...revealProps(reduceMotion, index * 0.06)}
                >
                  <span className={styles.equbAudienceSoonBadge}>
                    <span className={styles.equbComingSoonBadge}>Coming Soon</span>
                  </span>
                  <div className={styles.equbAudienceIconWrap}>
                    <SoonIcon aria-hidden />
                  </div>
                  <div className={styles.equbAudienceCardBody}>
                    <h4 className={styles.equbAudienceTitle}>{title}</h4>
                    <p className={styles.equbAudienceDesc}>{desc}</p>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </motion.section>

        <motion.section
          className={styles.einvoiceClose}
          {...revealProps(reduceMotion)}
        >
          <p className={styles.equbSectionLabel}>
            Arif Electronic Sales Registration System
          </p>
          <h2 className={styles.equbSectionTitle}>
            Your sales. Your invoices. Your business — connected.
          </h2>
        </motion.section>

        <div className={styles.einvoiceFaq}>
          <FAQSection
            title="FAQ"
            subtitle="Sales registration, audit logs, and your data"
            description="Answers drawn from the product, the audit log policy, and the exit and data portability policy."
            faqs={faqs}
            itemColumns={2}
          />
        </div>

        <div className={`${styles.einvoiceFaq} ${styles.einvoiceSupportFaq}`}>
          <FAQSection
            title="E-Invoice Support FAQ"
            subtitle=""
            description="Common e-invoice questions, error fixes, and what to share when you contact support."
            faqs={supportFaqs}
            itemColumns={2}
            extra={
              <div className={styles.einvoiceSupportChecklist}>
                <h3>When contacting support, share:</h3>
                <ul>
                  {supportChecklist.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p className={styles.einvoiceSupportRule}>
                  Do not share passwords, secret keys, or certificate files. If
                  the issue needs system, API, certificate, tax-rule, or
                  configuration investigation, contact support with the details
                  above.
                </p>
              </div>
            }
          />
        </div>

        <motion.section
          id="user-manual"
          className={styles.einvoiceManualSection}
          {...revealProps(reduceMotion)}
        >
          <div className={styles.topHeader}>
            <p className={styles.equbSectionLabel}>USER MANUAL</p>
            <h2 className={styles.equbSectionTitle}>
              E-Invoice User Manual
            </h2>
          </div>
          <p className={styles.einvoiceManualLead}>
            The manual walks accountants and branch admins through setup,
            certificate exchange, invoice registration, cash and credit
            receipts, withholding, corrections, and the SMS and email customers
            receive. Read it here, or download the PDF.
          </p>
          <div className={styles.einvoiceManualActions}>
            <a
              href={MANUAL_PATH}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.einvoiceManualBtn}
            >
              <FaBookOpen aria-hidden />
              Read in a new tab
            </a>
            <a
              href={MANUAL_PATH}
              download="E-Invoice_User_Manual.pdf"
              className={styles.einvoiceManualBtnSecondary}
            >
              <FaDownload aria-hidden />
              Download PDF
            </a>
          </div>
          <div className={styles.einvoiceManualFrame}>
            <Document
              file={MANUAL_PATH}
              onLoadSuccess={({ numPages: total }) => {
                setNumPages(total);
                setPageNumber(1);
              }}
              loading={<p className={styles.einvoiceManualStatus}>Opening the manual…</p>}
              error={
                <p className={styles.einvoiceManualStatus}>
                  The manual could not be displayed here. Use Download PDF or open it in a new tab.
                </p>
              }
            >
              <div
                ref={canvasWrapRef}
                className={styles.einvoiceManualCanvas}
                style={pageSlotHeight ? { minHeight: pageSlotHeight } : undefined}
              >
                <Page
                  pageNumber={pageNumber}
                  width={pageWidth}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  onRenderSuccess={rememberPageHeight}
                  loading={
                    <div
                      className={styles.einvoiceManualSlot}
                      style={pageSlotHeight ? { height: pageSlotHeight } : undefined}
                      aria-hidden
                    />
                  }
                />
              </div>
            </Document>
            {numPages && (
              <div className={styles.einvoiceManualNav}>
                <button
                  type="button"
                  onClick={() => setPageNumber((n) => Math.max(1, n - 1))}
                  disabled={pageNumber <= 1}
                >
                  Previous
                </button>
                <span>
                  Page {pageNumber} of {numPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPageNumber((n) => Math.min(numPages, n + 1))}
                  disabled={pageNumber >= numPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </motion.section>
      </div>
      <Footer />
    </div>
  );
};

export default EInvoice;
